import { Button, Table } from 'react-bootstrap';
import { IconEdit, IconListDetails } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../shared/permissions';

interface StockItem {
  id: number;
  stock_name: string;
  quantity_owned: number;
  quantity_available: number;
}

interface ProductStockTableProps {
  stockItems: StockItem[];
  trackingType: string;
  onEditStock: (stockItem: StockItem) => void;
}

const ProductStockTable = ({ stockItems, trackingType, onEditStock }: ProductStockTableProps) => {
  const { t } = useTranslation();
  const { hasPerm } = usePermissions();

  if (stockItems.length === 0) {
    return <div className="text-secondary p-3">{t('warehouses.products.notInStock')}</div>;
  }

  const isConsumable = trackingType === 'CONSUMABLE';
  const canEditStock = hasPerm('warehouses.change_stockitem') && !isConsumable;
  const canViewUnits = hasPerm('warehouses.view_itemunit');
  const showActions = canEditStock || (trackingType === 'SERIALIZED' && canViewUnits);

  return (
    <div className="table-responsive">
      <Table hover className="table-vcenter card-table mb-0">
        <thead>
          <tr>
            <th>{t('warehouses.items.warehouse')}</th>
            <th className="text-end">{t('warehouses.products.owned')}</th>
            <th className="text-end">{t('warehouses.products.available')}</th>
            {showActions && <th className="text-end">{t('common.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {stockItems.map((stockItem) => (
            <tr key={stockItem.id}>
              <td>{stockItem.stock_name}</td>
              {isConsumable ? (
                <td className="text-end text-muted fw-medium" colSpan={2} title={t('warehouses.products.unlimitedStock')}>∞</td>
              ) : (
                <>
                  <td className="text-end">{stockItem.quantity_owned}</td>
                  <td className="text-end">{stockItem.quantity_available}</td>
                </>
              )}
              {showActions && (
                <td className="text-end">
                  <div className="btn-group">
                    {trackingType === 'SERIALIZED' && canViewUnits && (
                      <Button
                        as={Link as any}
                        to={`/warehouses/stock-items/${stockItem.id}/units`}
                        size="sm"
                        variant="outline-secondary"
                        title={t('warehouses.products.viewUnits')}
                      >
                        <IconListDetails size={14} />
                      </Button>
                    )}
                    {canEditStock && (
                      <Button size="sm" variant="outline-primary" onClick={() => onEditStock(stockItem)} title={t('warehouses.products.editStock')}>
                        <IconEdit size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ProductStockTable;
